import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Card,
  CardBody,
  Alert,
  AlertIcon,
  useToast,
  Select,
  FormControl,
  FormLabel,
  Badge,
} from '@chakra-ui/react';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../lib/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { LeagueId, Match } from '../../types';
import {
  getMatchIdsForMundialJornada,
  MUNDIAL_GROUP_JORNADAS,
  parseLigaMxJornadaNumber,
} from '../../services/matchdayService';
import { ResponsiveTableContainer } from '../ui/ResponsiveTableContainer';
import {
  filterParticipatingUsers,
  isUserParticipating,
} from '../../services/userParticipationService';
import { hydrateMundialMatchesAsync } from '../../services/mundial/mundialKnockoutHydrationService';

const LEAGUE_NAMES: Record<LeagueId, string> = {
  liga_mx: 'Liga BBVA MX',
  mundial_fifa: 'Mundial FIFA 2026',
};

interface FirestoreUser {
  id: string;
  uuid: string;
  uid?: string;
  name: string;
  displayName?: string;
  participacion?: Partial<Record<LeagueId, boolean>>;
}

function countUniquePredictionsPerUser(
  predictions: { user_id: string; match_id: string }[],
  matchIdSet: Set<string>
): Record<string, number> {
  const uniqueByUser: Record<string, Set<string>> = {};

  for (const prediction of predictions) {
    if (!matchIdSet.has(prediction.match_id)) continue;
    if (!uniqueByUser[prediction.user_id]) {
      uniqueByUser[prediction.user_id] = new Set();
    }
    uniqueByUser[prediction.user_id].add(prediction.match_id);
  }

  return Object.fromEntries(
    Object.entries(uniqueByUser).map(([userId, matchIds]) => [userId, matchIds.size])
  );
}

function resolveUserPredictionCount(
  user: FirestoreUser,
  predictionCounts: Record<string, number>
): number {
  const candidateIds = [user.uuid, user.uid, user.id].filter(Boolean) as string[];
  for (const id of candidateIds) {
    if (predictionCounts[id] !== undefined) {
      return predictionCounts[id];
    }
  }
  return 0;
}

interface UserPredictions {
  id: string;
  uuid: string;
  name: string;
  total_predicciones: number;
  phase?: string;
}

const ViewQuinielas: React.FC = () => {
  const [league, setLeague] = useState<LeagueId>('liga_mx');
  const [usuariosCompletos, setUsuariosCompletos] = useState<UserPredictions[]>([]);
  const [usuariosFaltantes, setUsuariosFaltantes] = useState<UserPredictions[]>([]);
  const [usuariosRetirados, setUsuariosRetirados] = useState<UserPredictions[]>([]);
  const [, setLoading] = useState(true);
  const [phases, setPhases] = useState<string[]>([]);
  const [selectedPhase, setSelectedPhase] = useState('');
  const [selectedJornada, setSelectedJornada] = useState('');
  const [allMatches, setAllMatches] = useState<Match[]>([]);
  const [totalMatchesInJornada, setTotalMatchesInJornada] = useState(0);
  const toast = useToast();
  const { user } = useAuth();

  useEffect(() => {
    loadPhases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [league]);

  useEffect(() => {
    const hasSelection = league === 'mundial_fifa' ? selectedJornada : selectedPhase;
    if (hasSelection) {
      loadPredictions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPhase, selectedJornada, league]);

  const loadPhases = async () => {
    try {
      const matchesRef = collection(db, 'matches');
      const matchesSnap = await getDocs(matchesRef);
      const raw = matchesSnap.docs
        .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() } as Match))
        .filter((m) => (m.league ?? 'liga_mx') === league);

      const filtered =
        league === 'mundial_fifa' ? await hydrateMundialMatchesAsync(raw) : raw;

      setAllMatches(filtered);

      if (league === 'mundial_fifa') {
        setPhases([]);
        setSelectedPhase('');
        setSelectedJornada('');
        return;
      }

      const uniquePhases = Array.from(
        new Set(
          filtered
            .map((m) => m.phase)
            .filter((phase) => phase && parseLigaMxJornadaNumber(phase) !== null)
        )
      ).sort(
        (a, b) =>
          (parseLigaMxJornadaNumber(a) ?? 0) - (parseLigaMxJornadaNumber(b) ?? 0)
      );

      setPhases(uniquePhases);
      setSelectedPhase(uniquePhases[0] ?? '');
      setSelectedJornada('');
    } catch (error) {
      console.error('Error al cargar fases:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las jornadas',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const resolveMatchIds = (): string[] => {
    if (league === 'mundial_fifa') {
      const jornada = Number(selectedJornada);
      if (!jornada) return [];
      return getMatchIdsForMundialJornada(allMatches, jornada);
    }
    return allMatches.filter((m) => m.phase === selectedPhase).map((m) => m.id);
  };

  const loadPredictions = async () => {
    setLoading(true);
    try {
      const usersRef = collection(db, 'users');
      const usersSnap = await getDocs(usersRef);
      const allUsers = usersSnap.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          uuid: data.uuid,
          uid: data.uid,
          name: data.name || data.displayName || 'Usuario sin nombre',
          participacion: data.participacion,
          ...data,
        };
      }) as FirestoreUser[];

      const users = filterParticipatingUsers(allUsers, league);
      const withdrawn = allUsers.filter((u) => !isUserParticipating(u, league));

      const matchIds = resolveMatchIds();
      const totalMatchesInPhase = matchIds.length;
      setTotalMatchesInJornada(totalMatchesInPhase);

      if (matchIds.length === 0) {
        setUsuariosCompletos([]);
        setUsuariosFaltantes([]);
        setLoading(false);
        return;
      }

      const matchIdSet = new Set(matchIds);
      const predictionsRef = collection(db, 'predictions');
      const predictionsQuery = query(
        predictionsRef,
        where('match_id', 'in', matchIds)
      );
      const predictionsSnap = await getDocs(predictionsQuery);
      const predictionsOfLeague = predictionsSnap.docs
        .map((docSnap) => docSnap.data())
        .filter((data) => {
          const predLeague = data.league ?? 'liga_mx';
          return predLeague === league && data.match_id && data.user_id;
        })
        .map((data) => ({
          user_id: data.user_id as string,
          match_id: data.match_id as string,
        }));

      const predictionCounts = countUniquePredictionsPerUser(predictionsOfLeague, matchIdSet);

      // Separar usuarios con predicciones completas e incompletas
      const completos: UserPredictions[] = [];
      const faltantes: UserPredictions[] = [];

      users.forEach(user => {
        const totalPredicciones = resolveUserPredictionCount(user, predictionCounts);
        const userData = {
          id: user.id,
          uuid: user.uuid,
          name: user.name,
          total_predicciones: totalPredicciones,
          phase:
            league === 'mundial_fifa'
              ? `Jornada ${selectedJornada}`
              : selectedPhase,
        };

        if (totalPredicciones === totalMatchesInPhase) {
          completos.push(userData);
        } else {
          faltantes.push(userData);
        }
      });

      setUsuariosCompletos(completos);
      setUsuariosFaltantes(faltantes);
      setUsuariosRetirados(
        withdrawn.map((u) => ({
          id: u.id,
          uuid: u.uuid,
          name: u.name,
          total_predicciones: 0,
        }))
      );
      setLoading(false);

    } catch (error) {
      console.error('Error al cargar predicciones:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las predicciones',
        status: 'error',
        duration: 3000,
      });
      setLoading(false);
    }
  };

  if (user?.email !== 'htorres@teiker.mx') {
    return (
      <Box p={4}>
        <Heading size="md">No tienes acceso a esta página</Heading>
      </Box>
    );
  }

  return (
    <Container maxW="container.xl" py={5}>
      <Card mb={4}>
        <CardBody>
          <Heading as="h2" size="lg" textAlign="center" mb={4}>
            Estado de Pronósticos por Jornada - {LEAGUE_NAMES[league]}
          </Heading>
          <FormControl mb={4}>
            <FormLabel>Liga / Torneo</FormLabel>
            <Select value={league} onChange={(e) => setLeague(e.target.value as LeagueId)}>
              <option value="liga_mx">{LEAGUE_NAMES.liga_mx}</option>
              <option value="mundial_fifa">{LEAGUE_NAMES.mundial_fifa}</option>
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel>Seleccionar Jornada</FormLabel>
            {league === 'mundial_fifa' ? (
              <Select
                value={selectedJornada}
                onChange={(e) => setSelectedJornada(e.target.value)}
                placeholder="Selecciona la jornada"
              >
                {MUNDIAL_GROUP_JORNADAS.map((j) => (
                  <option key={j} value={String(j)}>
                    Jornada {j} — Fase de grupos
                  </option>
                ))}
              </Select>
            ) : (
              <Select
                value={selectedPhase}
                onChange={(e) => setSelectedPhase(e.target.value)}
                placeholder="Selecciona la jornada"
              >
                {phases.map((phase) => (
                  <option key={phase} value={phase}>
                    {phase}
                  </option>
                ))}
              </Select>
            )}
          </FormControl>
          {totalMatchesInJornada > 0 && (
            <Box mt={3} fontSize="sm" color="gray.600">
              Esta jornada tiene {totalMatchesInJornada} partidos. Solo se listan usuarios
              activos en {LEAGUE_NAMES[league]} (sin retirados).
            </Box>
          )}
        </CardBody>
      </Card>

      <Box mb={8}>
        <Heading as="h3" size="md" mb={4}>
          Usuarios que completaron la jornada ({totalMatchesInJornada} partidos)
        </Heading>
        <ResponsiveTableContainer>
        <Table variant="simple" colorScheme="gray">
          <Thead>
            <Tr>
              <Th>Usuario</Th>
              <Th isNumeric>Pronósticos capturados</Th>
            </Tr>
          </Thead>
          <Tbody>
            {usuariosCompletos.map(user => (
              <Tr key={user.id}>
                <Td>{user.name}</Td>
                <Td isNumeric>{user.total_predicciones}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        </ResponsiveTableContainer>
      </Box>

      <Box>
        <Heading as="h3" size="md" mb={4} color="red.500">
          Usuarios que faltan por capturar pronósticos
        </Heading>
        <ResponsiveTableContainer>
        <Table variant="simple" colorScheme="red">
          <Thead>
            <Tr>
              <Th>Usuario</Th>
              <Th isNumeric>Pronósticos capturados</Th>
            </Tr>
          </Thead>
          <Tbody>
            {usuariosFaltantes.length > 0 ? (
              usuariosFaltantes.map(user => (
                <Tr key={user.id}>
                  <Td>{user.name}</Td>
                  <Td isNumeric>{user.total_predicciones}</Td>
                </Tr>
              ))
            ) : (
              <Tr>
                <Td colSpan={2} textAlign="center">
                  <Alert status="success">
                    <AlertIcon />
                    ¡Todos los usuarios han capturado sus pronósticos para esta jornada!
                  </Alert>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
        </ResponsiveTableContainer>
      </Box>

      {usuariosRetirados.length > 0 && (
        <Box mt={8}>
          <Heading as="h3" size="md" mb={4} color="gray.500">
            Usuarios retirados de la quiniela ({usuariosRetirados.length})
          </Heading>
          <ResponsiveTableContainer>
          <Table variant="simple" colorScheme="gray">
            <Thead>
              <Tr>
                <Th>Usuario</Th>
                <Th>Estado</Th>
              </Tr>
            </Thead>
            <Tbody>
              {usuariosRetirados.map((user) => (
                <Tr key={user.id}>
                  <Td>{user.name}</Td>
                  <Td>
                    <Badge colorScheme="gray">Retirado</Badge>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
          </ResponsiveTableContainer>
        </Box>
      )}
    </Container>
  );
};

export default ViewQuinielas; 